import { useEffect, useState } from "react";

import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { Icon } from "@/components/atoms/Icons";
import { fmtUSD, parseDecimal } from "@/components/atoms/format";
import type { components } from "@/api/schema.gen";

type PositionView = components["schemas"]["PositionView"];
type BalanceView = components["schemas"]["BalanceView"];
type OrderView = components["schemas"]["OrderView"];

export function PortfolioView() {
  const userId = useStore((s) => s.userId);
  const [positions, setPositions] = useState<PositionView[]>([]);
  const [balances, setBalances] = useState<BalanceView[]>([]);
  const [openOrders, setOpenOrders] = useState<OrderView[]>([]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const load = async () => {
      const [pos, bal, ord] = await Promise.all([
        api.GET("/v1/positions", { params: { query: { limit: 100 } } }),
        api.GET("/v1/wallet/balances"),
        api.GET("/v1/orders", {
          params: { query: { status: "accepted", limit: 100 } },
        }),
      ]);
      if (cancelled) return;
      if (pos.data) setPositions(pos.data.items);
      if (bal.data) setBalances(bal.data.items);
      if (ord.data) setOpenOrders(ord.data.items);
    };
    load();
    const t = setInterval(load, 12_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [userId]);

  if (!userId) {
    return (
      <div className="card mx-6 my-6 flex flex-col items-center gap-3 py-16">
        <Icon.Briefcase size={28} className="text-muted" />
        <p className="text-[13px] text-muted">
          Connect a wallet to view your portfolio.
        </p>
      </div>
    );
  }

  const totalCost = positions.reduce(
    (acc, p) => acc + parseDecimal(p.cost_basis),
    0,
  );
  const realized = positions.reduce(
    (acc, p) => acc + parseDecimal(p.realized_pnl),
    0,
  );
  const cash = balances.find((b) => b.asset === "USDC");

  return (
    <div className="flex flex-col gap-4 px-4 pb-28 pt-4 sm:px-6 lg:pb-20">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi
          label="Open positions"
          value={positions.length.toString()}
        />
        <Kpi label="Cost basis" value={fmtUSD(totalCost, { compact: false })} />
        <Kpi
          label="Realized P/L"
          value={fmtUSD(realized, { compact: false })}
          tone={realized >= 0 ? "yes" : "no"}
        />
        <Kpi
          label="Cash (USDC)"
          value={cash ? fmtUSD(parseDecimal(cash.available), { compact: false }) : "—"}
        />
      </div>

      <section>
        <h3 className="mb-2 text-[12px] uppercase tracking-wider text-muted">
          Positions
        </h3>
        {positions.length === 0 ? (
          <div className="card flex items-center justify-center py-10 text-[13px] text-muted">
            No open positions.
          </div>
        ) : (
          <div className="card overflow-hidden p-0">
            <div
              className="grid items-center gap-3 border-b border-line bg-bg-2 px-4 py-2 text-[11px] uppercase tracking-wider text-muted"
              style={{
                gridTemplateColumns: "minmax(0,2fr) 100px 120px 120px 120px",
              }}
            >
              <span>Outcome</span>
              <span>Size</span>
              <span>Cost basis</span>
              <span>Realized P/L</span>
              <span>Updated</span>
            </div>
            {positions.map((p) => (
              <div
                key={`${p.user_id}-${p.outcome_id}`}
                className="grid items-center gap-3 border-b border-line px-4 py-3"
                style={{
                  gridTemplateColumns: "minmax(0,2fr) 100px 120px 120px 120px",
                }}
              >
                <span className="truncate font-mono text-[12px] text-text-2">
                  {p.outcome_id.slice(0, 8)}…
                </span>
                <span className="font-mono text-[13px]">
                  {parseDecimal(p.size)}
                </span>
                <span className="font-mono text-[13px]">
                  {fmtUSD(parseDecimal(p.cost_basis), { compact: false })}
                </span>
                <span
                  className="font-mono text-[13px]"
                  style={{
                    color:
                      parseDecimal(p.realized_pnl) >= 0
                        ? "var(--yes)"
                        : "var(--no)",
                  }}
                >
                  {fmtUSD(parseDecimal(p.realized_pnl), { compact: false })}
                </span>
                <span className="text-[12px] text-muted">
                  {new Date(p.updated_at).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="mb-2 text-[12px] uppercase tracking-wider text-muted">
          Open orders
        </h3>
        {openOrders.length === 0 ? (
          <div className="card flex items-center justify-center py-10 text-[13px] text-muted">
            No open orders.
          </div>
        ) : (
          <div className="card overflow-hidden p-0">
            {openOrders.map((o) => (
              <div
                key={o.id}
                className="grid items-center gap-3 border-b border-line px-4 py-3 text-[12.5px]"
                style={{ gridTemplateColumns: "auto auto 100px 100px 100px auto" }}
              >
                <span
                  className="font-mono uppercase"
                  style={{
                    color: o.side === "buy" ? "var(--yes)" : "var(--no)",
                  }}
                >
                  {o.side}
                </span>
                <span className="font-mono text-muted">{o.order_type}</span>
                <span className="font-mono">
                  {parseDecimal(o.price).toFixed(3)}
                </span>
                <span className="font-mono">
                  {parseDecimal(o.filled_size)}/{parseDecimal(o.size)}
                </span>
                <span className="text-muted">{o.status}</span>
                <button
                  className="btn btn-ghost"
                  onClick={async () => {
                    await api.DELETE("/v1/orders/{id}", {
                      params: { path: { id: o.id } },
                    });
                  }}
                >
                  Cancel
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "yes" | "no";
}) {
  return (
    <div className="card p-4">
      <div className="text-[11px] uppercase tracking-wider text-muted">
        {label}
      </div>
      <div
        className="mt-2 font-mono text-[22px] font-semibold"
        style={{
          color:
            tone === "yes"
              ? "var(--yes)"
              : tone === "no"
                ? "var(--no)"
                : "var(--text)",
        }}
      >
        {value}
      </div>
    </div>
  );
}
